import React from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import {
  BarChart3,
  Bot,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock,
  Columns2,
  Download,
  Eye,
  Flag,
  Globe2,
  LayoutGrid,
  Link as LinkIcon,
  Network,
  Plane,
  Search,
  SwatchBook,
  Users,
  Waypoints,
} from 'lucide-react';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { useInvestigationsAccess } from '../hooks/useInvestigationsAccess';
import { FeedbackButton } from './Feedback/FeedbackButton';
import { WordmarkTint } from './brand';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  selectedTab: string;
  onTabChange: (tabId: string) => void;
  onToggleCollapse: () => void;
  isAdmin?: boolean;
  canManageConnectivity?: boolean;
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  collapsed: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, collapsed, onClick }) => (
  <button
    className={`nav-item ${isActive ? 'active' : ''}`}
    onClick={onClick}
    title={collapsed ? label : undefined}
  >
    <span className="nav-item-icon">{icon}</span>
    <span className="nav-item-label">{label}</span>
  </button>
);

const ICON_SIZE = 16;
const ICON_STROKE = 1.6;

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  selectedTab,
  onTabChange,
  onToggleCollapse,
  isAdmin = false,
  canManageConnectivity = false,
}) => {
  const { features } = useTenantConfig();
  const investigationsAllowed = useInvestigationsAccess();

  const hasConnectionsSection = features.connection_analysis
    || features.schedule_comparison
    || features.hub_reports
    || features.partnership_analysis
    || features.bank_optimizer;

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight size={16} strokeWidth={ICON_STROKE} aria-hidden />
            : <ChevronLeft size={16} strokeWidth={ICON_STROKE} aria-hidden />}
        </button>
      </div>
      <div className="sidebar-content">
        <div className="nav-sections">
          {/* Workspaces Section */}
          {features.workspaces && (
            <CollapsibleSection
              title="Workspaces"
              icon="layout-grid"
              sidebarCollapsed={collapsed}
            >
              <NavItem
                icon={<LayoutGrid size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="Workspaces"
                isActive={selectedTab === 'workspaces'}
                collapsed={collapsed}
                onClick={() => onTabChange('workspaces')}
              />
              {(features.agent_api || investigationsAllowed) && <NavItem
                icon={<Bot size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="Agent"
                isActive={selectedTab === 'designer'}
                collapsed={collapsed}
                onClick={() => onTabChange('designer')}
              />}
            </CollapsibleSection>
          )}

          {/* Schedule Section */}
          <CollapsibleSection
            title="Schedule"
            icon="calendar"
            sidebarCollapsed={collapsed}
          >
            <NavItem
              icon={<Download size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
              label="Load"
              isActive={selectedTab === 'loader'}
              collapsed={collapsed}
              onClick={() => onTabChange('loader')}
            />
            {features.schedule_explorer && (
              <NavItem
                icon={<Search size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="View"
                isActive={selectedTab === 'explorer' || selectedTab === 'calculator'}
                collapsed={collapsed}
                onClick={() => onTabChange('explorer')}
              />
            )}
            {features.schedule_visualize && (
              <NavItem
                icon={<Eye size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="Visualize"
                isActive={selectedTab === 'visualize'}
                collapsed={collapsed}
                onClick={() => onTabChange('visualize')}
              />
            )}
          </CollapsibleSection>

          {/* Flight Connections Section */}
          {hasConnectionsSection && (
            <CollapsibleSection
              title="Flight Connections"
              icon="flows"
              sidebarCollapsed={collapsed}
            >
              {features.connection_analysis && (
                <NavItem
                  icon={<Network size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                  label="Analyse"
                  isActive={selectedTab === 'multiple-flight-pairs'}
                  collapsed={collapsed}
                  onClick={() => onTabChange('multiple-flight-pairs')}
                />
              )}
              {features.schedule_comparison && (
                <NavItem
                  icon={<Columns2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                  label="Compare"
                  isActive={selectedTab === 'schedule-comparison'}
                  collapsed={collapsed}
                  onClick={() => onTabChange('schedule-comparison')}
                />
              )}
              {features.partnership_analysis && (
                <NavItem
                  icon={<LinkIcon size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                  label="Partnership"
                  isActive={selectedTab === 'partnership-analysis'}
                  collapsed={collapsed}
                  onClick={() => onTabChange('partnership-analysis')}
                />
              )}
              {features.hub_reports && (
                <NavItem
                  icon={<CircleDot size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                  label="Hub Reports"
                  isActive={selectedTab === 'hub-analysis'}
                  collapsed={collapsed}
                  onClick={() => onTabChange('hub-analysis')}
                />
              )}
              {features.bank_optimizer && (
                <NavItem
                  icon={<BarChart3 size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                  label="Bank Optimizer"
                  isActive={selectedTab === 'hub-optimizer'}
                  collapsed={collapsed}
                  onClick={() => onTabChange('hub-optimizer')}
                />
              )}
            </CollapsibleSection>
          )}

          {/* User Settings Section */}
          <CollapsibleSection
            title="User Settings"
            icon="cog"
            sidebarCollapsed={collapsed}
          >
            <NavItem
              icon={<Globe2 size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
              label="Regions"
              isActive={selectedTab === 'region-definitions'}
              collapsed={collapsed}
              onClick={() => onTabChange('region-definitions')}
            />
            <NavItem
              icon={<Flag size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
              label="Countries"
              isActive={selectedTab === 'country-definitions'}
              collapsed={collapsed}
              onClick={() => onTabChange('country-definitions')}
            />
            <NavItem
              icon={<Plane size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
              label="Airports"
              isActive={selectedTab === 'airport-definitions'}
              collapsed={collapsed}
              onClick={() => onTabChange('airport-definitions')}
            />
            {features.od_itinerary_compare && canManageConnectivity && (
              <NavItem icon={<Waypoints size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="Connectivity rules" isActive={selectedTab === 'connectivity-rules'}
                collapsed={collapsed} onClick={() => onTabChange('connectivity-rules')} />
            )}
            {features.mct_management && (
              <NavItem
                icon={<Clock size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="MCT"
                isActive={selectedTab === 'minimum-connection-times'}
                collapsed={collapsed}
                onClick={() => onTabChange('minimum-connection-times')}
              />
            )}
            <NavItem
              icon={<SwatchBook size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
              label="Preferences"
              isActive={selectedTab === 'user-preferences'}
              collapsed={collapsed}
              onClick={() => onTabChange('user-preferences')}
            />
            {isAdmin && (
              <NavItem
                icon={<Users size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
                label="Admin"
                isActive={selectedTab === 'admin'}
                collapsed={collapsed}
                onClick={() => onTabChange('admin')}
              />
            )}
          </CollapsibleSection>
          {/* Feedback (#641, #1062): its own item under Settings, outside the
              collapsible sections so it stays visible; hidden when Sentry
              isn't configured. */}
          <FeedbackButton collapsed={collapsed} />
        </div>
      </div>
      <div className="sidebar-signature" aria-hidden={collapsed}>
        <WordmarkTint height={16} className="sidebar-signature__mark" />
      </div>
    </div>
  );
};
